import React, { useEffect, useState } from "react";

import { getCropRecommendations } from "services";

import { formatNumericCurrency, useCancelAsyncOnUnmount, useTranslation } from "utils";

import { CurrencyCode } from "consts";

import { PRACTICE_SLUG, PracticeCrop } from "models";

import { Button, Card, Flex, Icon, RadioChangeEvent, RadioPill, RadioPillGroup, Spin } from "components";

// CropRecommendationCardProps Figma reference:
// https://www.figma.com/file/Jc9kcxKurjLuMvmNgxtE6e/Design-Library?node-id=14509%3A124760&mode=dev

type SideCropRecommendationResponse = {
  sideCropId: string;
  payout: { min: number; max: number };
};

type CropRecommendationCardProps = {
  currency?: CurrencyCode;
  cropId: string;
  crops: PracticeCrop[];
  dataTestId?: string;
  fieldId: string;
  fieldIsInRedzone: boolean;
  onAddCrop: (cropId: string) => void;
  onCropRecommendationSelected: (sideCropRecommendationResponse: SideCropRecommendationResponse) => void;
  onRecommendationDismissed: () => void;
  resetCropRecommendationSelection: () => void;
};

enum COMPONENT_STATES {
  DEFAULT = "DEFAULT",
  DISMISSED = "DISMISSED",
  LOADING = "LOADING",
  OPEN = "OPEN",
  NO_RESULTS = "NO_RESULTS",
}

export const CropRecommendationCard = ({
  currency,
  crops,
  cropId,
  dataTestId,
  fieldId,
  fieldIsInRedzone,
  onAddCrop,
  onCropRecommendationSelected,
  onRecommendationDismissed,
  resetCropRecommendationSelection,
}: CropRecommendationCardProps) => {
  const signal = useCancelAsyncOnUnmount()();
  const { t } = useTranslation(["modules", "practices"]);

  const [componentState, setComponentState] = useState<COMPONENT_STATES>(COMPONENT_STATES.NO_RESULTS);
  const [recommendations, setRecommendations] = useState<SideCropRecommendationResponse[]>([]);
  const [selectedRecommendationId, setSelectedRecommendationId] = useState<string>();

  const formatPayoutValue = (value: number): string => {
    if (!value) return formatNumericCurrency(0, { currency, decimals: 0 });
    return formatNumericCurrency(value, { currency, decimals: 0, signDisplay: "always" });
  };

  useEffect(() => {
    getCropRecommendations({ cropId, fieldId, signal }).then((res) => {
      const topRecommendations = res.sort((a, b) => b.payout.max - a.payout.max).slice(0, 3);
      setRecommendations(topRecommendations);

      if (!topRecommendations.length) {
        setComponentState(COMPONENT_STATES.NO_RESULTS);
        return;
      }

      setComponentState(COMPONENT_STATES.LOADING);

      setTimeout(() => {
        setComponentState(COMPONENT_STATES.OPEN);
      }, 3000);
    });
  }, [cropId, fieldId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddRecommendation = () => {
    if (!selectedRecommendationId) return;
    onAddCrop(selectedRecommendationId);
    setRecommendations([]);
    setSelectedRecommendationId("");
    setComponentState(COMPONENT_STATES.NO_RESULTS);
  };

  const handleDismissal = () => {
    setRecommendations([]);
    setSelectedRecommendationId("");
    resetCropRecommendationSelection();
    setComponentState(COMPONENT_STATES.DISMISSED);
    onRecommendationDismissed();
  };

  const handleSelectSideCrop = (e: RadioChangeEvent) => {
    setSelectedRecommendationId(e.target.value);
    const selectedRecommendation = recommendations.find((recommendation) => recommendation.sideCropId === e.target.value);
    if (!selectedRecommendation) return;
    onCropRecommendationSelected(selectedRecommendation);
  };

  // reset the parent component crop recommendation selection when the component gets unmounted
  useEffect(
    // eslint-disable-next-line arrow-body-style
    () => {
      return () => {
        resetCropRecommendationSelection();
      };
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const shouldDisplayCard = ![COMPONENT_STATES.NO_RESULTS, COMPONENT_STATES.DISMISSED].includes(componentState);

  return shouldDisplayCard ? (
    <Card data-testid={`${dataTestId}-information-card`} className="crop-recommendation-card" mixpanelText="crop-recommendation-card">
      {componentState === COMPONENT_STATES.LOADING && (
        <Flex align="center" gap="1rem" data-testid="crop-recommendation-card-loader">
          <Spin color="#100C06" />
          <p className="paragraph-m-bold">{t("cropRecommendationCard.loading.title")}</p>
        </Flex>
      )}
      {componentState === COMPONENT_STATES.DEFAULT && (
        <>
          <Flex align="center" justify="space-between">
            <Flex align="center" gap="1rem">
              <Icon iconName="tabler/bulb" />
              <p className="paragraph-m-bold">{t("cropRecommendationCard.open.title")}</p>
            </Flex>
            <Icon className="pointer" iconName="tabler/chevron-down" onClick={() => setComponentState(COMPONENT_STATES.OPEN)} />
          </Flex>
        </>
      )}
      {componentState === COMPONENT_STATES.OPEN && (
        <>
          <Flex align="center" justify="space-between">
            <Flex align="center" gap="1rem">
              <Icon iconName="tabler/bulb" />
              <p className="paragraph-m-bold">{t("cropRecommendationCard.open.title")}</p>
            </Flex>
            <Icon className="pointer" iconName="tabler/chevron-up" onClick={() => setComponentState(COMPONENT_STATES.DEFAULT)} />
          </Flex>
          <div className="content">
            <p className="paragraph-m margin-bottom-100">{t("cropRecommendationCard.open.description")}</p>
            <RadioPillGroup className="margin-bottom-200" onChange={handleSelectSideCrop}>
              {recommendations.map(({ payout, sideCropId }) => (
                <RadioPill
                  dataTestId={`recommendation-card-form-radio-group-${sideCropId}`}
                  additionalInfo={!fieldIsInRedzone ? `(${t("cropRecommendationCard.open.upTo")} ${formatPayoutValue(payout.max)})` : undefined}
                  className="recommendation-card-form"
                  key={sideCropId}
                  label={t(`${PRACTICE_SLUG.CROPS}.${crops.find((crop) => crop.id === sideCropId)?.translationSlug}`)}
                  value={sideCropId}
                />
              ))}
            </RadioPillGroup>
            <Flex align="center" className="button-container" justify="flex-end" gap="1rem">
              <Button size="small" type="text" onClick={handleDismissal}>
                {t("cropRecommendationCard.open.notInterested")}
              </Button>
              <Button size="small" type="primary" disabled={!selectedRecommendationId} onClick={handleAddRecommendation}>
                {t("cropRecommendationCard.open.addCrop")}
              </Button>
            </Flex>
          </div>
        </>
      )}
    </Card>
  ) : null;
};
